"use client";

import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  ArrowRight,
  Loader2,
  TrendingUp,
  FileText,
  AlertTriangle,
  BarChart3,
  CheckCircle,
  XCircle,
  DollarSign,
  Building,
  Activity,
  Users,
  FileCheck,
  Gavel,
  Search,
} from "lucide-react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { formatCurrency } from "../lib/textron-data-processor";
import { getCompanyName } from "../lib/textron-data-processor";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface PartnerReviewDashboardProps {
  setActiveSection?: (section: string) => void;
}

interface ScopingSummary {
  exceedsMateriality: number;
  consideredSignificant: number;
  qualitativelyMaterial: number;
  qualitativelyMaterialNotExceeding: number;
}

interface FinancialTrend {
  date: string;
  totalAssets: number;
  totalLiabilities: number;
  borrowings: number;
  totalRevenue: number;
  profitLoss: number;
}

interface WorkspaceSummary {
  workspaceName: string;
  nonNrpmmRisks: number;
  controlsTested: number;
  proceduresPerformed: number;
  deficienciesCount: number;
  misstatementsCount: number;
}

interface Deficiency {
  id: string;
  deficiencyType: string;
  description: string | null;
}

interface Misstatement {
  id: string;
  type: string;
  workspace: string;
  amount: number;
}

interface SignificantRisk {
  rommId: string;
  workspace: string;
  description: string;
  controlIds: string[];
  procedureIds: string[];
}

interface LitigationRow {
  subjectMatter: string;
  forum: string;
  amountInvolved: string;
  description: string;
  managementResponse: string;
  responseOfLegalCounsel: string;
}

interface ConfirmationAndSamplingSummary {
  totalConfirmations: number;
  confirmationsReceived: number;
  confirmationsPending: number;
  confirmationSamplingEntries: number;
  samplingLibraryEntries: number;
}

export function PartnerReviewDashboard({ setActiveSection }: PartnerReviewDashboardProps = {}) {
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState<string>("");
  const [scopingSummary, setScopingSummary] = useState<ScopingSummary>({
    exceedsMateriality: 0,
    consideredSignificant: 0,
    qualitativelyMaterial: 0,
    qualitativelyMaterialNotExceeding: 0,
  });
  const [financialTrends, setFinancialTrends] = useState<FinancialTrend[]>([]);
  const [workspaceSummaries, setWorkspaceSummaries] = useState<WorkspaceSummary[]>([]);
  const [deficiencies, setDeficiencies] = useState<Deficiency[]>([]);
  const [misstatements, setMisstatements] = useState<Misstatement[]>([]);
  const [significantRisks, setSignificantRisks] = useState<SignificantRisk[]>([]);
  const [materialityData, setMaterialityData] = useState({
    planningMateriality: 0,
    performanceMateriality: 0,
    clearlyTrivialThreshold: 0,
  });
  const [litigationsTable, setLitigationsTable] = useState<LitigationRow[]>([]);
  const [clrNonComplianceTable, setClrNonComplianceTable] = useState<any[]>([]);
  const [showClrTable, setShowClrTable] = useState(false);
  const [rembEstimatesTable, setRembEstimatesTable] = useState<any[]>([]);
  const [se4Table, setSe4Table] = useState<any[]>([]);
  const [confirmationAndSamplingSummary, setConfirmationAndSamplingSummary] =
    useState<ConfirmationAndSamplingSummary>({
      totalConfirmations: 0,
      confirmationsReceived: 0,
      confirmationsPending: 0,
      confirmationSamplingEntries: 0,
      samplingLibraryEntries: 0,
    });
  const lastLoadedEngagementId = useRef<string | null>(null);
  const isLoadingRef = useRef(false);

  const loadPlanningResponsesFromFile = async (resolvedCompanyName: string) => {
    const result = await (window as any).sharePointAPI?.downloadClientJson?.("Planning_UserResponses.json");
    if (!(result?.success && result?.data)) return null;

    const dataArray = Array.isArray(result.data) ? result.data : [result.data];
    const sectionMap: Record<string, any> = {};
    for (const sectionData of dataArray) {
      if (!sectionData || typeof sectionData !== "object") continue;
      if (
        sectionData.companyName &&
        resolvedCompanyName &&
        sectionData.companyName !== resolvedCompanyName
      ) {
        continue;
      }
      const key = sectionData.sectionKey || sectionData.sectionName;
      if (key && sectionData.responses) {
        sectionMap[String(key)] = sectionData.responses;
      }
    }
    return sectionMap;
  };

  const loadTailoringAreaFromFile = async (areaName: string) => {
    const [executionRes, concludingRes] = await Promise.all([
      (window as any).sharePointAPI?.downloadClientJson?.("Execution_TailoringQuestions_Responses.json"),
      (window as any).sharePointAPI?.downloadClientJson?.("ConcludingProcedures_TailoringQuestions_Responses.json"),
    ]);
    return (
      executionRes?.data?.[areaName] ??
      concludingRes?.data?.[areaName] ??
      null
    );
  };

  useEffect(() => {
    if (!currentEngagement) {
      setLoading(false);
      lastLoadedEngagementId.current = null;
      return;
    }

    // Prevent loading if already loading or if we've already loaded data for this engagement
    if (isLoadingRef.current || lastLoadedEngagementId.current === currentEngagement.id) {
      return;
    }

    const loadAllData = async () => {
      isLoadingRef.current = true;
      lastLoadedEngagementId.current = currentEngagement.id;
      setLoading(true);
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");


        await Promise.all([
          loadScopingSummary(name),
          loadFinancialTrends(),
          loadWorkspaceSummaries(),
          loadDeficiencies(),
          loadMisstatements(),
          loadSignificantRisks(),
          loadMateriality(name),
          loadLitigationsTable(),
          loadCLRNonComplianceTable(name),
          loadREMBEstimatesTable(name),
          loadSE4Table(name),
          loadConfirmationAndSamplingSummary(),
        ]);
      } catch (error) {
        console.error("Error loading partner review data:", error);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };

    loadAllData();
  }, [currentEngagement]);

  const loadConfirmationAndSamplingSummary = async () => {
    try {
      const [
        confirmationResponseRes,
        confirmationActivityRes,
        confirmationSamplingRes,
        samplingLibraryRes,
      ] = await Promise.all([
        (window as any).sharePointAPI?.downloadClientJson?.("Confirmation_Response.json"),
        (window as any).sharePointAPI?.downloadClientJson?.("Confirmation_ActivityLog.json"),
        (window as any).sharePointAPI?.downloadClientJson?.("Confirmation_SamplingLog.json"),
        (window as any).sharePointAPI?.downloadClientJson?.("Sampling_SamplingLog.json"),
      ]);

      const confirmationsData =
        confirmationResponseRes?.success &&
        confirmationResponseRes?.data &&
        typeof confirmationResponseRes.data === "object"
          ? confirmationResponseRes.data
          : {};
      const confirmationIds = Object.keys(confirmationsData);
      const totalConfirmations = confirmationIds.length;

      const timeline = Array.isArray(confirmationActivityRes?.data?.timeline)
        ? confirmationActivityRes.data.timeline
        : [];
      const receivedIds = new Set<string>();
      timeline.forEach((entry: any) => {
        const action = String(entry?.action || "").trim().toLowerCase();
        const letterId = String(entry?.letter_id || "").trim();
        if (action === "confirmation received" && letterId) {
          receivedIds.add(letterId);
        }
      });
      const confirmationsReceived = confirmationIds.filter((id) => receivedIds.has(String(id))).length;
      const confirmationsPending = Math.max(0, totalConfirmations - confirmationsReceived);

      const countSamplingEntries = (raw: any): number => {
        if (!raw || typeof raw !== "object") return 0;
        let count = 0;
        Object.values(raw).forEach((workspace: any) => {
          if (!workspace || typeof workspace !== "object") return;
          Object.values(workspace).forEach((entries: any) => {
            if (Array.isArray(entries)) count += entries.length;
          });
        });
        return count;
      };

      const confirmationSamplingEntries = countSamplingEntries(
        confirmationSamplingRes?.success ? confirmationSamplingRes?.data : {}
      );
      const samplingLibraryEntries = countSamplingEntries(
        samplingLibraryRes?.success ? samplingLibraryRes?.data : {}
      );

      setConfirmationAndSamplingSummary({
        totalConfirmations,
        confirmationsReceived,
        confirmationsPending,
        confirmationSamplingEntries,
        samplingLibraryEntries,
      });
    } catch (error) {
      console.error("Error loading confirmation and sampling summary:", error);
      setConfirmationAndSamplingSummary({
        totalConfirmations: 0,
        confirmationsReceived: 0,
        confirmationsPending: 0,
        confirmationSamplingEntries: 0,
        samplingLibraryEntries: 0,
      });
    }
  };

  const loadLitigationsTable = async () => {
    try {
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const result = await (window as any).sharePointAPI.downloadClientJson("ConcludingProcedures_LIT_Substantive.json");
        if (result?.success && result.data) {
          const data = result.data;
          // The litigations table is stored in substantiveProcedures.litigations
          const substantiveProcedures = data.substantiveProcedures || {};
          const litigations = substantiveProcedures.litigations;
          if (Array.isArray(litigations)) {
            setLitigationsTable(litigations);
          } else {
            setLitigationsTable([]);
          }
        }
      }
    } catch (error) {
      console.error("Error loading litigations table:", error);
    }
  };

  const loadCLRNonComplianceTable = async (companyName: string) => {
    try {
      const responses = await loadTailoringAreaFromFile("CLR");
      if (!responses) {
        setShowClrTable(false);
        setClrNonComplianceTable([]);
        return;
      }
      const clr41Response = responses["CLR-4-1"];
      if (clr41Response?.selectedOption === "Yes") {
        setShowClrTable(true);
        const tableData = responses["CLR-4-1-table"];
        if (tableData?.table && Array.isArray(tableData.table)) {
          setClrNonComplianceTable(tableData.table);
        } else {
          setClrNonComplianceTable([]);
        }
      } else {
        setShowClrTable(false);
        setClrNonComplianceTable([]);
      }
    } catch (error) {
      console.error("Error loading CLR non-compliance table:", error);
      setShowClrTable(false);
      setClrNonComplianceTable([]);
    }
  };

  const loadREMBEstimatesTable = async (companyName: string) => {
    try {
      const responses = await loadTailoringAreaFromFile("REMB");
      const remb1Data = responses?.["REMB-1"];
      if (remb1Data?.table && Array.isArray(remb1Data.table)) {
        setRembEstimatesTable(remb1Data.table);
      } else {
        setRembEstimatesTable([]);
      }
    } catch (error) {
      console.error("Error loading REMB estimates table:", error);
      setRembEstimatesTable([]);
    }
  };

  const loadSE4Table = async (companyName: string) => {
    try {
      const responses = await loadTailoringAreaFromFile("SUBSEV");
      const se4Data = responses?.["SE-4"];
      if (se4Data?.table && Array.isArray(se4Data.table)) {
        setSe4Table(se4Data.table);
      } else {
        setSe4Table([]);
      }
    } catch (error) {
      console.error("Error loading SE-4 table:", error);
      setSe4Table([]);
    }
  };

  const loadMateriality = async (companyName: string) => {
    try {
      const planningSections = await loadPlanningResponsesFromFile(companyName);
      const responses =
        planningSections?.materiality ||
        planningSections?.Materiality ||
        null;
      if (!responses) return;
      const m9Response = responses["M-9"];
      const useRevisedMateriality = m9Response?.subAnswer === "Yes";

      let planningMateriality = 0;
      let performanceMateriality = 0;
      let clearlyTrivialThreshold = 0;

      if (useRevisedMateriality) {
        const revisedMaterialityResponse = responses["revisedMateriality"];
        if (revisedMaterialityResponse) {
          planningMateriality = parseFloat(revisedMaterialityResponse.determinedMateriality || "0") || 0;
          performanceMateriality = parseFloat(revisedMaterialityResponse.performanceMateriality || "0") || 0;
          clearlyTrivialThreshold = parseFloat(revisedMaterialityResponse.trivialThreshold || "0") || 0;
        }
      } else {
        const m3Response = responses["M-3"];
        if (m3Response?.textResponse) {
          const textValue = typeof m3Response.textResponse === "string"
            ? m3Response.textResponse
            : m3Response.textResponse.plainText || m3Response.textResponse.html || "0";
          planningMateriality = parseFloat(textValue) || 0;
        }

        const m5Response = responses["M-5"];
        if (m5Response?.overridePerformanceMateriality !== undefined) {
          performanceMateriality = parseFloat(m5Response.overridePerformanceMateriality.toString()) || 0;
        }

        const m8Response = responses["M-8"];
        if (m8Response?.textResponse) {
          const textValue = typeof m8Response.textResponse === "string"
            ? m8Response.textResponse
            : m8Response.textResponse.plainText || m8Response.textResponse.html || "0";
          clearlyTrivialThreshold = parseFloat(textValue) || 0;
        }
      }

      setMaterialityData({
        planningMateriality,
        performanceMateriality,
        clearlyTrivialThreshold,
      });
    } catch (error) {
      console.error("Error loading materiality:", error);
    }
  };

  const loadScopingSummary = async (companyName: string) => {
    try {
      const planningSections = await loadPlanningResponsesFromFile(companyName);
      const responses =
        planningSections?.["preliminary-analytical"] ||
        planningSections?.["Preliminary Analytical Procedures"] ||
        null;
      if (!responses) return;
      const balanceSheetItems = responses["PreliminaryAnalyticalProcedures_BalanceSheetItems"] || [];
      const profitLossItems = responses["PreliminaryAnalyticalProcedures_ProfitLossItems"] || [];
      const allItems = [...(Array.isArray(balanceSheetItems) ? balanceSheetItems : []), ...(Array.isArray(profitLossItems) ? profitLossItems : [])];

      let materiality = 0;
      const matResponses =
        planningSections?.materiality ||
        planningSections?.Materiality ||
        null;
      if (matResponses) {
        const m9Response = matResponses["M-9"];
        const useRevised = m9Response?.subAnswer === "Yes";
        if (useRevised) {
          const revisedMat = matResponses["revisedMateriality"];
          materiality = parseFloat(revisedMat?.determinedMateriality || "0") || 0;
        } else {
          const m3Response = matResponses["M-3"];
          if (m3Response?.textResponse) {
            const textValue = typeof m3Response.textResponse === "string"
              ? m3Response.textResponse
              : m3Response.textResponse.plainText || m3Response.textResponse.html || "0";
            materiality = parseFloat(textValue) || 0;
          }
        }
      }

      let exceedsMateriality = 0;
      let consideredSignificant = 0;
      let qualitativelyMaterial = 0;
      let qualitativelyMaterialNotExceeding = 0;

      allItems.forEach((item: any) => {
        const exceeds = item.exceeds_materiality === true || item.exceeds_materiality === "Yes";
        const significant = item.considered_significant === "Significant";
        const qualMaterial = item.qualitatively_material === "Yes" || item.qualitatively_material === true;

        // Count ABCOTDs Exceeding Materiality
        if (exceeds) {
          exceedsMateriality++;
        }

        // Count Considered Significant (all items where considered_significant is "Significant", regardless of exceeds_materiality)
        if (significant) {
          consideredSignificant++;
        }

        // Count Qualitatively Material (Exceeding) - where qualitatively_material is "Yes" and exceeds_materiality is true
        if (qualMaterial && exceeds) {
          qualitativelyMaterial++;
        }

        // Count Qualitatively Material (Not Exceeding) - where qualitatively_material is "Yes" but exceeds_materiality is false
        if (qualMaterial && !exceeds) {
          qualitativelyMaterialNotExceeding++;
        }
      });

      setScopingSummary({
        exceedsMateriality,
        consideredSignificant,
        qualitativelyMaterial,
        qualitativelyMaterialNotExceeding,
      });
    } catch (error) {
      console.error("Error loading scoping summary:", error);
    }
  };

  const loadFinancialTrends = async () => {
    try {
      // Load FinData_Formulae.json from SharePoint
      if ((window as any).sharePointAPI?.downloadClientJson) {
        const result = await (window as any).sharePointAPI.downloadClientJson("FinData_Formulae.json");
        if (result?.success && result.data) {
          const data = result.data;
          const trends: FinancialTrend[] = [];

          // Extract dates and values
          const dates = Object.keys(data).filter(key => !key.startsWith("_"));
          dates.forEach((date) => {
            const dateData = data[date];
              if (dateData) {
              // Try lowercase underscore format first (bs_as, pl_as_ye/pl_as), then fallback to uppercase hyphen format (BS-AS, PL-AS)
              const bsData = dateData["bs_as"] || dateData["BS-AS"] || {};
              const plData = dateData["pl_as_ye"] || dateData["pl_as"] || dateData["PL-AS"] || {};

              const totalAssets = Math.abs(parseFloat(bsData["Total Assets"] || "0") || 0);
              const totalLiabilities = Math.abs(parseFloat(bsData["Total Liabilities"] || "0") || 0);
              const longTermBorrowings = Math.abs(parseFloat(bsData["Long-term borrowings"] || "0") || 0);
              const shortTermBorrowings = Math.abs(parseFloat(bsData["Short-term borrowings"] || "0") || 0);
              const borrowings = longTermBorrowings + shortTermBorrowings;
              const totalRevenue = Math.abs(parseFloat(plData["TOTAL REVENUE"] || "0") || 0);
              const profitLoss = parseFloat(plData["PROFIT/(LOSS) FOR THE PERIOD"] || "0") || 0;

              trends.push({
                date,
                totalAssets,
                totalLiabilities,
                borrowings,
                totalRevenue,
                profitLoss,
              });
            }
          });

          // Sort by date ascending (dd/mm/yy format): e.g. 31/03/24, 31/03/25, 30/09/25
          const parseDdMmYy = (dateStr: string) => {
            const parts = dateStr.trim().split("/");
            if (parts.length !== 3) return new Date(0);
            const dd = parseInt(parts[0], 10);
            const mm = parseInt(parts[1], 10) - 1;
            const yy = parseInt(parts[2], 10);
            const year = yy < 100 ? 2000 + yy : yy;
            return new Date(year, mm, dd);
          };
          trends.sort((a, b) => parseDdMmYy(a.date).getTime() - parseDdMmYy(b.date).getTime());
          setFinancialTrends(trends);
        }
      }
    } catch (error) {
      console.error("Error loading financial trends:", error);
    }
  };

  const loadWorkspaceSummaries = async () => {
    try {
      // Load Sections.json to get execution workspaces
      let executionWorkspaces: string[] = [];
      if ((window as any).sharePointAPI?.downloadSectionsJson) {
        const sectionsRes = await (window as any).sharePointAPI.downloadSectionsJson();
        if (sectionsRes.success && sectionsRes.data?.Execution) {
          executionWorkspaces = Object.keys(sectionsRes.data.Execution);
        }
      }

      // Load ROMMs
      let romms: any[] = [];
      if ((window as any).sharePointAPI?.readRommLibrary) {
        const rommRes = await (window as any).sharePointAPI.readRommLibrary();
        if (rommRes.success && rommRes.data?.romm_library) {
          romms = Array.isArray(rommRes.data.romm_library) ? rommRes.data.romm_library : [];
        }
      }

      // Load Controls
      let controls: any[] = [];
      if ((window as any).sharePointAPI?.downloadRcmJson) {
        const rcmRes = await (window as any).sharePointAPI.downloadRcmJson();
        if (rcmRes.success && rcmRes.data) {
          const librariesRcm = rcmRes.data.libraries_RCM || rcmRes.data.libraries_rcm || {};
          controls = Object.entries(librariesRcm).map(([controlId, entry]: [string, any]) => ({
            controlId,
            workspace: entry.workspace || [],
          }));
        }
      }

      // Load SubstantiveProcedures.json to count procedures per workspace
      let substantiveProcedures: Record<string, Record<string, any>> = {};
      try {
        const { loadSubstantiveProcedures } = await import("../utils/substantive-procedures-utils");
        substantiveProcedures = await loadSubstantiveProcedures();
      } catch (error) {
        console.warn("Failed to load SubstantiveProcedures.json for procedure counts:", error);
      }

      // Load Libraries_Deficiency.json for deficiency counts per workspace (by control_id)
      let deficienciesByControlId: Array<{ control_id: string[] }> = [];
      if ((window as any).sharePointAPI?.downloadDeficiencyJson) {
        const defRes = await (window as any).sharePointAPI.downloadDeficiencyJson();
        if (defRes?.success && defRes.data?.libraries_deficiency) {
          const raw = defRes.data.libraries_deficiency as Record<string, any>;
          deficienciesByControlId = Object.values(raw).map((v: any) => ({
            control_id: Array.isArray(v.control_id) ? v.control_id : (v.control_id != null ? [String(v.control_id)] : []),
          }));
        }
      }

      // Load Libraries_Misstatement.json for misstatement counts per workspace (by workspace field)
      let misstatementsByWorkspace: Array<{ workspace: string }> = [];
      if ((window as any).sharePointAPI?.downloadMisstatementJson) {
        const misRes = await (window as any).sharePointAPI.downloadMisstatementJson();
        if (misRes?.success && misRes.data) {
          const lib = misRes.data.Libraries_Misstatement || misRes.data.libraries_Misstatement || {};
          const misObj = lib.misstatements || {};
          misstatementsByWorkspace = Object.values(misObj).map((v: any) => ({
            workspace: v.workspace != null ? String(v.workspace) : "",
          }));
        }
      }

      // Create a mapping of workspace names to execution section IDs
      const workspaceToSectionMap: Record<string, string> = {
        "Employee Benefits Expense": "execution-payroll",
        "Property, Plant & Equipment": "execution-ppe",
        "Intangible Assets": "execution-ia",
        "Depreciation": "execution-depreciation",
        "Other Assets": "execution-other-assets",
        "Other Financial Assets": "execution-other-financial-assets",
        "Inventories": "execution-inv",
        "Cash and Cash Equivalents": "execution-cce",
        "Financial Costs": "execution-fincst",
        "Income Tax": "execution-inctx",
        "Investments": "execution-invsmt",
        "Provisions, Contingent Liabilities and Contingent Assets": "execution-pcl",
        "Lease Liability": "execution-lease-liability",
        "Lease Liability - Ind AS": "execution-lease-liability-ind-as",
        "Right-of-Use Assets": "execution-right-of-use-assets",
        "Impairment": "execution-impairment",
        "Trade Receivables": "execution-trade-receivables",
        "Borrowings": "execution-borrowings",
        "Other Expenses": "execution-other-expenses",
        "Other Liabilities": "execution-other-liabilities",
        "Other Financial Liabilities": "execution-other-financial-liabilities",
        "Other Receivables": "execution-other-receivables",
        "Going Concern": "execution-going-concern",
        "Litigations & Claims": "execution-litigations-claims",
        "Opening Balance Testing": "execution-opening-balance-testing",
        "Related Party Disclosure": "execution-related-party-disclosure",
        "Equity": "execution-equity",
        "Management Use of Expert": "execution-management-use-of-expert",
        "Use of Auditors Specialist": "execution-use-of-auditors-specialist",
        "Compliance with Laws and Regulations": "execution-compliance-with-laws-and-regulations",
        "Review of Estimates for Management Bias": "execution-review-of-estimates-for-management-bias",
        "Internal Audit": "execution-internal-audit",
        "Capital or Other Maintenance": "execution-comc",
        "Financial Closing & Reporting Process": "execution-fcrp",
        "Management Override of Controls": "execution-moc",
      };

      const summaries: WorkspaceSummary[] = executionWorkspaces.map((workspace) => {
        // Count non-NRPMM risks
        const workspaceRomms = romms.filter((romm) => {
          const workspaceArray = Array.isArray(romm.workspace) ? romm.workspace : romm.workspace ? [romm.workspace] : [];
          return workspaceArray.includes(workspace);
        });

        const nonNrpmmRisks = workspaceRomms.filter((romm) => {
          const assessment = (romm.assesment || romm.assessment || "").toLowerCase();
          return !assessment.includes("nrpmm");
        }).length;

        // Count all controls that have this workspace in their workspace field
        const workspaceControls = controls.filter((c) => {
          const wsArray = Array.isArray(c.workspace) ? c.workspace : c.workspace ? [c.workspace] : [];
          return wsArray.includes(workspace);
        });

        // Count substantive procedures for this workspace from SubstantiveProcedures.json
        const workspaceProcedures = substantiveProcedures[workspace] || {};
        const proceduresPerformed = Object.keys(workspaceProcedures).length;

        // Deficiency count: deficiencies whose control_id matches any control in this workspace
        const workspaceControlIds = workspaceControls.map((c: { controlId: string }) => c.controlId);
        const deficienciesCount = deficienciesByControlId.filter((d) =>
          d.control_id.some((cid) => workspaceControlIds.includes(cid))
        ).length;

        // Misstatement count: misstatements whose workspace field equals this workspace name
        const misstatementsCount = misstatementsByWorkspace.filter((m) => m.workspace === workspace).length;

        return {
          workspaceName: workspace,
          nonNrpmmRisks,
          controlsTested: workspaceControls.length,
          proceduresPerformed,
          deficienciesCount,
          misstatementsCount,
        };
      });

      setWorkspaceSummaries(summaries);
    } catch (error) {
      console.error("Error loading workspace summaries:", error);
    }
  };

  const loadDeficiencies = async () => {
    try {
      if ((window as any).sharePointAPI?.downloadDeficiencyJson) {
        const res = await (window as any).sharePointAPI.downloadDeficiencyJson();
        if (res.success && res.data) {
          const raw = res.data.libraries_deficiency || {};
          const items: Deficiency[] = Object.entries(raw).map(([id, value]: [string, any]) => ({
            id,
            deficiencyType: value.deficiency_type || "",
            description: value.description || null,
          }));
          setDeficiencies(items);
        }
      }
    } catch (error) {
      console.error("Error loading deficiencies:", error);
    }
  };

  const loadMisstatements = async () => {
    try {
      if ((window as any).sharePointAPI?.downloadMisstatementJson) {
        const res = await (window as any).sharePointAPI.downloadMisstatementJson();
        if (res.success && res.data) {
          const librariesMisstatement = res.data.Libraries_Misstatement || res.data.libraries_Misstatement || {};
          const misstatementsObj = librariesMisstatement.misstatements || {};
          const items: Misstatement[] = Object.entries(misstatementsObj).map(([id, value]: [string, any]) => {
            // Get the misstatement amount from the type-specific misstatement field
            // Structure: value.factual.misstatement, value.judgmental.misstatement, or value.projected.misstatement
            const type = value.type || "Factual";
            const typeKey = type.toLowerCase(); // "factual", "judgmental", or "projected"
            const typeData = value[typeKey] || {};
            const misstatementAmount = typeData.misstatement || "0";
            
            return {
              id,
              type,
              workspace: value.workspace || "",
              amount: parseFloat(misstatementAmount) || 0,
            };
          });
          setMisstatements(items);
        }
      }
    } catch (error) {
      console.error("Error loading misstatements:", error);
    }
  };

  const handleNavigateToPreliminaryAnalytical = () => {
    if (setActiveSection) {
      setActiveSection("planning-preliminary-analytical");
    }
  };

  const handleNavigateToFinancialData = () => {
    if (setActiveSection) {
      setActiveSection("overview");
    }
  };

  const handleNavigateToWorkspace = (workspaceName: string) => {
    if (setActiveSection) {
      // Mapping of workspace names to execution section IDs
      const workspaceMap: Record<string, string> = {
        "Employee Benefits Expense": "execution-payroll",
        "Property, Plant & Equipment": "execution-ppe",
        "Intangible Assets": "execution-ia",
        "Depreciation": "execution-depreciation",
        "Other Assets": "execution-other-assets",
        "Other Financial Assets": "execution-other-financial-assets",
        "Inventories": "execution-inv",
        "Cash and Cash Equivalents": "execution-cce",
        "Financial Costs": "execution-fincst",
        "Income Tax": "execution-inctx",
        "Investments": "execution-invsmt",
        "Provisions, Contingent Liabilities and Contingent Assets": "execution-pcl",
        "Lease Liability": "execution-lease-liability",
        "Lease Liability - Ind AS": "execution-lease-liability-ind-as",
        "Right-of-Use Assets": "execution-right-of-use-assets",
        "Impairment": "execution-impairment",
        "Trade Receivables": "execution-trade-receivables",
        "Borrowings": "execution-borrowings",
        "Other Expenses": "execution-other-expenses",
        "Other Liabilities": "execution-other-liabilities",
        "Other Financial Liabilities": "execution-other-financial-liabilities",
        "Other Receivables": "execution-other-receivables",
        "Going Concern": "execution-going-concern",
        "Litigations & Claims": "execution-litigations-claims",
        "Opening Balance Testing": "execution-opening-balance-testing",
        "Related Party Disclosure": "execution-related-party-disclosure",
        "Equity": "execution-equity",
        "Management Use of Expert": "execution-management-use-of-expert",
        "Use of Auditors Specialist": "execution-use-of-auditors-specialist",
        "Compliance with Laws and Regulations": "execution-compliance-with-laws-and-regulations",
        "Review of Estimates for Management Bias": "execution-review-of-estimates-for-management-bias",
        "Internal Audit": "execution-internal-audit",
        "Capital or Other Maintenance": "execution-comc",
        "Financial Closing & Reporting Process": "execution-fcrp",
        "Management Override of Controls": "execution-moc",
      };
      
      const section = workspaceMap[workspaceName];
      if (section) {
        setActiveSection(section);
      } else {
        // For dynamic workspaces, encode the workspace name
        // Format: dynamic-workspace:Execution:prefix:encodedName
        const encodedName = encodeURIComponent(workspaceName);
        setActiveSection(`dynamic-workspace:Execution::${encodedName}`);
      }
    }
  };

  const handleNavigateToDeficiency = (deficiencyId: string) => {
    if (setActiveSection) {
      // Navigate to deficiency library first, then the library page will handle showing the detail
      setActiveSection("deficiency-library");
      // Note: The actual deficiency detail view would need to be handled by the library page
      // This is a limitation - we'd need to pass the deficiencyId somehow
    }
  };

  const loadSignificantRisks = async () => {
    try {
      // Load ROMMs from Libraries_Romm.json
      let romms: any[] = [];
      if ((window as any).sharePointAPI?.readRommLibrary) {
        const rommRes = await (window as any).sharePointAPI.readRommLibrary();
        if (rommRes.success && rommRes.data?.romm_library) {
          romms = Array.isArray(rommRes.data.romm_library) ? rommRes.data.romm_library : [];
        }
      }

      // Filter for significant risks
      const significant = romms.filter((romm) => {
        const assessment = (romm.assesment || romm.assessment || "").toLowerCase();
        return assessment.includes("significant");
      });

      // Transform to SignificantRisk format
      const risks: SignificantRisk[] = significant.map((romm) => {
        // Get romm_id
        const rommId = romm["romm-id"] || romm.romm_id || "";

        // Get workspace (can be array or string)
        const workspace = Array.isArray(romm.workspace)
          ? romm.workspace[0] || ""
          : romm.workspace || "";

        // Get description
        const description = romm.description || "";

        // Get control_id (can be array or string)
        const controlIds = Array.isArray(romm.control_id)
          ? romm.control_id.map(String)
          : romm.control_id
          ? [String(romm.control_id)]
          : [];

        // Get procedure_id (can be array or string)
        const procedureIds = Array.isArray(romm.procedure_id)
          ? romm.procedure_id.map(String)
          : romm.procedure_id
          ? [String(romm.procedure_id)]
          : [];

        return {
          rommId,
          workspace,
          description,
          controlIds,
          procedureIds,
        };
      });

      setSignificantRisks(risks);
    } catch (error) {
      console.error("Error loading significant risks:", error);
    }
  };

  const handleNavigateToMisstatement = (misstatementId: string, workspaceName: string) => {
    if (setActiveSection) {
      // Navigate to the workspace misstatements page
      // Format: {workspace}-misstatements
      const workspaceMap: Record<string, string> = {
        "Employee Benefits Expense": "payroll-misstatements",
        "Property, Plant & Equipment": "ppe-misstatements",
        "Intangible Assets": "ia-misstatements",
        "Depreciation": "depr-misstatements",
        "Other Assets": "othas-misstatements",
        "Other Financial Assets": "ofa-misstatements",
        "Inventories": "inv-misstatements",
        "Cash and Cash Equivalents": "cce-misstatements",
        "Financial Costs": "fincst-misstatements",
        "Income Tax": "inctx-misstatements",
        "Investments": "invsmt-misstatements",
        "Provisions, Contingent Liabilities and Contingent Assets": "pcl-misstatements",
        "Lease Liability": "llas-misstatements",
        "Lease Liability - Ind AS": "ll-misstatements",
        "Right-of-Use Assets": "rou-misstatements",
        "Impairment": "imp-misstatements",
        "Trade Receivables": "trdrcv-misstatements",
        "Borrowings": "bor-misstatements",
        "Other Expenses": "othexp-misstatements",
        "Other Liabilities": "othlib-misstatements",
        "Other Financial Liabilities": "ofllib-misstatements",
        "Other Receivables": "othrec-misstatements",
        "Going Concern": "golib-misstatements",
        "Litigations & Claims": "litclm-misstatements",
        "Opening Balance Testing": "opnbal-misstatements",
        "Related Party Disclosure": "relpard-misstatements",
        "Equity": "eqt-misstatements",
        "Management Use of Expert": "mgtexp-misstatements",
        "Use of Auditors Specialist": "useadtr-misstatements",
        "Compliance with Laws and Regulations": "cmplr-misstatements",
        "Review of Estimates for Management Bias": "rvestmb-misstatements",
        "Internal Audit": "intadt-misstatements",
        "Capital or Other Maintenance": "comc-misstatements",
        "Financial Closing & Reporting Process": "fcrp-misstatements",
        "Management Override of Controls": "moc-misstatements",
      };
      
      const section = workspaceMap[workspaceName];
      if (section) {
        setActiveSection(section);
      } else {
        // For dynamic workspaces
        const encodedName = encodeURIComponent(workspaceName);
        setActiveSection(`dynamic-workspace-misstatements:Execution::${encodedName}`);
      }
      // Note: The user will need to click on the specific misstatement in the list to view details
    }
  };

  const handleNavigateToRomm = (workspaceName: string) => {
    if (setActiveSection) {
      // Mapping of workspace names to ROMMs section IDs
      const workspaceMap: Record<string, string> = {
        "Employee Benefits Expense": "payroll-romms",
        "Property, Plant & Equipment": "ppe-romms",
        "Intangible Assets": "ia-romms",
        "Depreciation": "depr-romms",
        "Other Assets": "othas-romms",
        "Inventories": "inv-romms",
        "Cash and Cash Equivalents": "cce-romms",
        "Financial Costs": "fincst-romms",
        "Income Tax": "inctx-romms",
        "Investments": "invsmt-romms",
        "Provisions, Contingent Liabilities and Contingent Assets": "pcl-romms",
        "Lease Liability": "ll-as-romms",
        "Lease Liability - Ind AS": "ll-romms",
        "Right-of-Use Assets": "rou-romms",
        "Impairment": "imp-romms",
        "Trade Receivables": "trdrcv-romms",
        "Borrowings": "bor-romms",
        "Other Expenses": "othexp-romms",
        "Other Liabilities": "othlib-romms",
        "Other Financial Liabilities": "ofllib-romms",
        "Other Receivables": "othrec-romms",
        "Going Concern": "golib-romms",
        "Litigations & Claims": "litclm-romms",
        "Opening Balance Testing": "opnbal-romms",
        "Related Party Disclosure": "relpard-romms",
        "Equity": "eqt-romms",
        "Management Use of Expert": "mgtexp-romms",
        "Use of Auditors Specialist": "useadtr-romms",
        "Compliance with Laws and Regulations": "cmplr-romms",
        "Review of Estimates for Management Bias": "rvestmb-romms",
        "Internal Audit": "intadt-romms",
        "Capital or Other Maintenance": "comc-romms",
        "Financial Closing & Reporting Process": "fcrp-romms",
        "Management Override of Controls": "moc-romms",
      };
      
      const section = workspaceMap[workspaceName];
      if (section) {
        setActiveSection(section);
      } else {
        // For dynamic workspaces, use the dynamic workspace ROMMs pattern
        const encodedName = encodeURIComponent(workspaceName);
        setActiveSection(`dynamic-workspace-romms:Execution::${encodedName}`);
      }
    }
  };

  const totalMisstatementAmount = misstatements.reduce((sum, m) => sum + Math.abs(m.amount), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          <span className="text-white">Loading partner review dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Partner Review Dashboard</h1>
            <p className="text-white/60">Comprehensive review summary for {companyName || "the engagement"}</p>
          </div>
          <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/30 px-4 py-1.5 text-sm">
            Partner Review
          </Badge>
        </div>

        {/* 1. Scoping Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-blue-600/20 flex items-center justify-center">
                  <Search className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Scoping Summary</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Preliminary Analytical Procedures</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNavigateToPreliminaryAnalytical}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                View Details <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-lg bg-gradient-to-br from-red-500/10 to-red-600/5 border border-red-500/20">
                <p className="text-2xl font-bold text-white mb-1">{scopingSummary.exceedsMateriality}</p>
                <p className="text-xs text-white/60">ABCOTDs Exceeding Materiality</p>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20">
                <p className="text-2xl font-bold text-white mb-1">{scopingSummary.consideredSignificant}</p>
                <p className="text-xs text-white/60">Considered Significant</p>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20">
                <p className="text-2xl font-bold text-white mb-1">{scopingSummary.qualitativelyMaterial}</p>
                <p className="text-xs text-white/60">Qualitatively Material (Exceeding)</p>
              </div>
              <div className="p-4 rounded-lg bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20">
                <p className="text-2xl font-bold text-white mb-1">{scopingSummary.qualitativelyMaterialNotExceeding}</p>
                <p className="text-xs text-white/60">Qualitatively Material (Not Exceeding)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 2. Financial Position Trends */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-green-500/30 to-green-600/20 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Financial Position Trends</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Key Financial Metrics Over Time</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNavigateToFinancialData}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                View Financial Data <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {financialTrends.length > 0 ? (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={financialTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                    <XAxis dataKey="date" stroke="#ffffff60" />
                    <YAxis stroke="#ffffff60" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "rgba(0, 0, 0, 0.9)",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        borderRadius: "8px",
                        color: "#fff",
                      }}
                      formatter={(value: number) => formatCurrency(value)}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="totalAssets" stroke="#4da3ff" strokeWidth={2} name="Total Assets" />
                    <Line type="monotone" dataKey="totalLiabilities" stroke="#ef4444" strokeWidth={2} name="Total Liabilities" />
                    <Line type="monotone" dataKey="borrowings" stroke="#f59e0b" strokeWidth={2} name="Borrowings" />
                    <Line type="monotone" dataKey="totalRevenue" stroke="#10b981" strokeWidth={2} name="Total Revenue" />
                    <Line type="monotone" dataKey="profitLoss" stroke="#8b5cf6" strokeWidth={2} name="Profit/(Loss)" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-80 flex items-center justify-center text-white/40">
                No financial trend data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3. Execution Workspace Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500/30 to-purple-600/20 flex items-center justify-center">
                <Activity className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Execution Workspace Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Risk, Control & Procedure Status by Workspace</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {workspaceSummaries.length === 0 ? (
              <div className="py-8 text-center text-white/50 text-sm">
                No execution workspace data available
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-white/10">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="text-white/80 font-semibold">Workspace</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-28">RoMMs</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-28">Controls</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-40 whitespace-normal px-2">Substantive Procedures</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-32">Deficiencies</TableHead>
                      <TableHead className="text-white/80 font-semibold text-center w-28">Misstatements</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workspaceSummaries.map((summary) => (
                      <TableRow
                        key={summary.workspaceName}
                        className="border-white/5 hover:bg-white/5"
                      >
                        <TableCell
                          className="text-[#4da3ff] font-medium cursor-pointer hover:underline hover:text-[#4da3ff]/90"
                          onClick={() => handleNavigateToWorkspace(summary.workspaceName)}
                        >
                          {summary.workspaceName}
                        </TableCell>
                        <TableCell className="text-white/90 text-center w-28 tabular-nums">{summary.nonNrpmmRisks}</TableCell>
                        <TableCell className="text-white/90 text-center w-28 tabular-nums">{summary.controlsTested}</TableCell>
                        <TableCell className="text-white/90 text-center w-40 tabular-nums">{summary.proceduresPerformed}</TableCell>
                        <TableCell className="text-white/90 text-center w-32 tabular-nums">{summary.deficienciesCount}</TableCell>
                        <TableCell className="text-white/90 text-center w-28 tabular-nums">{summary.misstatementsCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 4. Significant Risks */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500/30 to-red-600/20 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Significant Risks Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Risks assessed as significant</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {significantRisks.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Risk ID</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Workspace</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Description</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Control(s)</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Substantive Procedures</th>
                    </tr>
                  </thead>
                  <tbody>
                    {significantRisks.map((risk) => (
                      <tr
                        key={risk.rommId}
                        className="border-b border-white/5 hover:bg-white/5"
                      >
                        <td className="py-3 px-4 text-white font-medium">{risk.rommId}</td>
                        <td className="py-3 px-4 text-white/80">{risk.workspace || "N/A"}</td>
                        <td className="py-3 px-4 text-white/70">{risk.description || "N/A"}</td>
                        <td className="py-3 px-4 text-white/70">
                          {risk.controlIds.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {risk.controlIds.map((controlId, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs border-white/20 text-white/70">
                                  {controlId}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td className="py-3 px-4 text-white/70">
                          {risk.procedureIds.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {risk.procedureIds.map((procedureId, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs border-white/20 text-white/70">
                                  {procedureId}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No significant risks found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 5. Control Deficiency Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500/30 to-orange-600/20 flex items-center justify-center">
                  <XCircle className="h-5 w-5 text-orange-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Control Deficiency Summary</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Deficiencies Identified</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  if (setActiveSection) {
                    setActiveSection("deficiency-library");
                  }
                }}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                View Deficiency Library <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Deficiency ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Deficiency Type</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {deficiencies.length > 0 ? (
                    deficiencies.map((def) => (
                      <tr
                        key={def.id}
                        className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                        onClick={() => handleNavigateToDeficiency(def.id)}
                      >
                        <td className="py-3 px-4 text-white">{def.id}</td>
                        <td className="py-3 px-4 text-white/80">{def.deficiencyType || "N/A"}</td>
                        <td className="py-3 px-4 text-white/70">{def.description || "N/A"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-white/40">
                        No deficiencies found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* 6. Misstatement Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-yellow-500/30 to-yellow-600/20 flex items-center justify-center">
                <FileText className="h-5 w-5 text-yellow-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Misstatement Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Identified Misstatements</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto mb-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Misstatement ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Type</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-white/70">Workspace</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-white/70">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {misstatements.length > 0 ? (
                    misstatements.map((mis) => (
                      <tr
                        key={mis.id}
                        className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                        onClick={() => {
                          if (setActiveSection) {
                            setActiveSection("misstatement-library");
                          }
                        }}
                      >
                        <td className="py-3 px-4 text-white">{mis.id}</td>
                        <td className="py-3 px-4 text-white/80">{mis.type}</td>
                        <td className="py-3 px-4 text-white/70">{mis.workspace || "N/A"}</td>
                        <td className="py-3 px-4 text-right text-white">{formatCurrency(mis.amount)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-8 text-center text-white/40">
                        No misstatements found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {misstatements.length > 0 && (
              <div className="mt-4 p-4 rounded-lg bg-white/5 border border-white/10">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-white/60 mb-1">Total Misstatement Amount</p>
                    <p className="text-lg font-bold text-white">{formatCurrency(totalMisstatementAmount)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">Planning Materiality</p>
                    <p className="text-lg font-bold text-white">{formatCurrency(materialityData.planningMateriality)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">Performance Materiality</p>
                    <p className="text-lg font-bold text-white">{formatCurrency(materialityData.performanceMateriality)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/60 mb-1">Clearly Trivial Threshold</p>
                    <p className="text-lg font-bold text-white">{formatCurrency(materialityData.clearlyTrivialThreshold)}</p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 6. Summary of Accounting Estimates */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500/30 to-purple-600/20 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Summary of Accounting Estimates</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Estimates from Review of Estimates for Management Bias</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : rembEstimatesTable.length > 0 ? (
              <div className="space-y-3">
                <div className="rounded-md border border-white/10 bg-black/20 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-white/5">
                        <TableHead className="text-white/80 font-medium">Name of Estimate</TableHead>
                        <TableHead className="text-white/80 font-medium">ABCOTD</TableHead>
                        <TableHead className="text-white/80 font-medium">Estimation Uncertainty</TableHead>
                        <TableHead className="text-white/80 font-medium">Whether Management has used an expert</TableHead>
                        <TableHead className="text-white/80 font-medium">Includes fair valuation?</TableHead>
                        <TableHead className="text-white/80 font-medium">Whether auditor's specialist in involved?</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rembEstimatesTable.map((row, index) => (
                        <TableRow key={index} className="border-white/10 hover:bg-white/5">
                          <TableCell className="text-white/90 py-3 px-4">{row.col_0 || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4">{row.col_1 || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4">{row.col_2 || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4">{row.col_3 || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4">{row.col_4 || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4">{row.col_5 || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {setActiveSection && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActiveSection("concluding-review-estimates-bias")}
                      className="text-white/80 hover:text-white border-white/10"
                    >
                      View Details
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No estimates data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 7. Litigations & Claims Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-red-500/30 to-red-600/20 flex items-center justify-center">
                <Gavel className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Litigations & Claims Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Litigations from Assessment</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : litigationsTable.length > 0 ? (
              <div className="space-y-3">
                <div className="rounded-md border border-white/10 bg-black/20 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-white/5">
                        <TableHead className="text-white/80 font-medium">Subject Matter</TableHead>
                        <TableHead className="text-white/80 font-medium">Forum</TableHead>
                        <TableHead className="text-white/80 font-medium">Amount Involved</TableHead>
                        <TableHead className="text-white/80 font-medium">Description</TableHead>
                        <TableHead className="text-white/80 font-medium">Management Response</TableHead>
                        <TableHead className="text-white/80 font-medium">Response of Legal Counsel</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {litigationsTable.map((row, index) => (
                        <TableRow key={index} className="border-white/10 hover:bg-white/5">
                          <TableCell className="text-white/90 py-3 px-4">{row.subjectMatter || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4">{row.forum || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4">{row.amountInvolved || "-"}</TableCell>
                          <TableCell className="text-white/90 py-3 px-4 max-w-xs">
                            <div className="line-clamp-2">{row.description || "-"}</div>
                          </TableCell>
                          <TableCell className="text-white/90 py-3 px-4 max-w-xs">
                            <div className="line-clamp-2">{row.managementResponse || "-"}</div>
                          </TableCell>
                          <TableCell className="text-white/90 py-3 px-4 max-w-xs">
                            <div className="line-clamp-2">{row.responseOfLegalCounsel || "-"}</div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {setActiveSection && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setActiveSection("concluding-litigations-claims")}
                      className="text-white/80 hover:text-white border-white/10"
                    >
                      View Details
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-32 text-white/40">
                <p>No litigations data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 8. Non-Compliance with Laws and Regulations Table */}
        {showClrTable && (
          <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500/30 to-orange-600/20 flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-orange-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Non-Compliance with Laws and Regulations</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Instances of non-compliance identified</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-white/40" />
                </div>
              ) : clrNonComplianceTable.length > 0 ? (
                <div className="space-y-3">
                  <div className="rounded-md border border-white/10 bg-black/20 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          <TableHead className="text-white/80 font-medium">Nature of non-compliance</TableHead>
                          <TableHead className="text-white/80 font-medium">Our understanding of non-compliance</TableHead>
                          <TableHead className="text-white/80 font-medium">Evaluation of possible effect on financial statements</TableHead>
                          <TableHead className="text-white/80 font-medium">Evaluation in relation to other aspects of audit</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {clrNonComplianceTable.map((row, index) => (
                          <TableRow key={index} className="border-white/10 hover:bg-white/5">
                            <TableCell className="text-white/90 py-3 px-4">{row.nature_of_non_compliance || "-"}</TableCell>
                            <TableCell className="text-white/90 py-3 px-4 max-w-xs">
                              <div className="line-clamp-3">{row.our_understanding_of_non_compliance || "-"}</div>
                            </TableCell>
                            <TableCell className="text-white/90 py-3 px-4 max-w-xs">
                              <div className="line-clamp-3">{row.evaluation_of_possible_effect_on_financial_statements || "-"}</div>
                            </TableCell>
                            <TableCell className="text-white/90 py-3 px-4 max-w-xs">
                              <div className="line-clamp-3">{row.evaluation_in_relation_to_other_aspects_of_audit || "-"}</div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {setActiveSection && (
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setActiveSection("concluding-compliance-laws-regulations")}
                        className="text-white/80 hover:text-white border-white/10"
                      >
                        View Details
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-white/40">
                  <p>No non-compliance instances recorded</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 9. Summary of Subsequent Events */}
        {se4Table.length > 0 && (
          <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-blue-600/20 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-white text-lg">Summary of Subsequent Events</CardTitle>
                  <p className="text-xs text-white/50 mt-0.5">Events between financial statement date and audit report</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin text-white/40" />
                </div>
              ) : se4Table.length > 0 ? (
                <div className="space-y-3">
                  <div className="rounded-md border border-white/10 bg-black/20 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10 hover:bg-white/5">
                          <TableHead className="text-white/80 font-medium">Description of event</TableHead>
                          <TableHead className="text-white/80 font-medium">Does it require adjustment?</TableHead>
                          <TableHead className="text-white/80 font-medium">Does it require disclosure?</TableHead>
                          <TableHead className="text-white/80 font-medium">Basis of Assessment</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {se4Table.map((row, index) => (
                          <TableRow key={index} className="border-white/10 hover:bg-white/5">
                            <TableCell className="text-white/90 py-3 px-4">{row.descriptionOfEvent || "-"}</TableCell>
                            <TableCell className="text-white/90 py-3 px-4">{row.requiresAdjustment || "-"}</TableCell>
                            <TableCell className="text-white/90 py-3 px-4">{row.requiresDisclosure || "-"}</TableCell>
                            <TableCell className="text-white/90 py-3 px-4 max-w-xs">
                              <div className="line-clamp-3">{row.basisOfAssessment || "-"}</div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {setActiveSection && (
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setActiveSection("concluding-subsequent-events")}
                        className="text-white/80 hover:text-white border-white/10"
                      >
                        View Details
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32 text-white/40">
                  <p>No subsequent events recorded</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* 9. Confirmations and Samples Summary */}
        <Card className="border-white/10 bg-white/5 backdrop-blur-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500/30 to-blue-600/20 flex items-center justify-center">
                <FileCheck className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-white text-lg">Confirmations and Samples Summary</CardTitle>
                <p className="text-xs text-white/50 mt-0.5">Summary from confirmation and sampling libraries</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-3">
                <p className="text-xl font-bold text-white">{confirmationAndSamplingSummary.totalConfirmations}</p>
                <p className="text-xs text-white/60">Total confirmations</p>
              </div>
              <div className="rounded-lg border border-green-500/20 bg-green-500/10 p-3">
                <p className="text-xl font-bold text-white">{confirmationAndSamplingSummary.confirmationsReceived}</p>
                <p className="text-xs text-white/60">Confirmations received</p>
              </div>
              <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                <p className="text-xl font-bold text-white">{confirmationAndSamplingSummary.confirmationsPending}</p>
                <p className="text-xs text-white/60">Confirmations pending</p>
              </div>
              <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 p-3">
                <p className="text-xl font-bold text-white">{confirmationAndSamplingSummary.confirmationSamplingEntries}</p>
                <p className="text-xs text-white/60">Confirmation sampling entries</p>
              </div>
              <div className="rounded-lg border border-purple-500/20 bg-purple-500/10 p-3">
                <p className="text-xl font-bold text-white">{confirmationAndSamplingSummary.samplingLibraryEntries}</p>
                <p className="text-xs text-white/60">Sampling library entries</p>
              </div>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}

